import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { config } from '@uhg-abyss/web/tools/config';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Breadcrumbs } from '@uhg-abyss/web/ui/Breadcrumbs';
import { useGetCredentials } from '@src/hooks/useGetCredentials';
import { useDSUserInfo } from '@src/hooks/useDsUserInfo';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { resetSecertAlertData } from '@src/hooks/useRegenerateSecret';
import { styled } from '@uhg-abyss/web/tools/styled';
import { event } from '@uhg-abyss/web/tools/event';
import { Box } from './Box';
import { RegenerateSecretModal } from './RegenerateSecretModal/RegenerateSecretModal';
import { OAuthToken } from '../Credentials/OAuthToken';
import { mapBreadcrumbsData } from '@src/common/Utils/utils';

const Wrapper = styled('div', {
  '@screen >= $xs': {
    padding: '16px',
  },
  '@screen >= $xl': {
    padding: '32px 0 0 0',
  },
});

const StyledFlex = styled('div', {
  display: 'flex',
  flexBasis: '75%',
});

const Container = styled('div', {
  flexBasis: '100%',
});

const PageHeadingContainer = styled('div', {
  display: 'flex',
  marginTop: '8px',
  marginBottom: '32px',
  flexBasis: '50%',
});

const PageHeading = styled('h1', {
  color: 'rgb(0, 38, 119)',
  fontSize: '36px !important',
  fontWeight: '600',
  minHeight: '40px',
  lineHeight: '40px',
  marginTop: '8px',
});

const PageDesc = styled('div', {
  color: 'rgb(51, 51, 51)',
  fontSize: '16px',
  fontWeight: 500,
  letterSpacing: '0px',
  lineHeight: '24px',
  marginBottom: '16px',
});

const Flexed = styled('div', {
  '@screen >=$xs': {
    display: 'block',
  },
  '@screen >=$xl': {
    display: 'flex',
  },
});

const BoxWrapper = styled('div', {
  display: 'flex',
  marginTop: '27px',

  '@screen >= $xs': {
    flexDirection: 'column',
    gap: '16px',
  },

  '@screen >= $xl': {
    flexDirection: 'row',
    gap: '93px',
  },
});

export const CredentialsV2 = () => {
  const generatedSecret = useSelector((state) => {
    return state.GET_GENERATED_SECRET;
  });
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const [credentials, getCredentials] = useGetCredentials();
  const [dsUserInfo, getDSUserInfo] = useDSUserInfo();
  const [regenSecretModalOpen, setRegenSecretModalOpen] = useState(false);
  const [regenSecretEnv, setRegenSecretEnv] = useState('stage');
  const [isLoading, setIsLoading] = useState(true);
  const [, getResetSecertAlertData] = resetSecertAlertData();
  const [runCredentials, setRunCredentials] = useState(false);
  const [isPageLoadTriggered, setIsPageLoadTriggered] = useState(false);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);

  const showCredentials =
    dsUserInfo?.data?.status && dsUserInfo?.data?.status === 'ACTIVE';

  const breadcrumbsData = useSelector((state) => {
    return state.BREADCRUMBS.data;
  });

  useEffect(() => {
    // Use the breadcrumbs data from Redux
    if (breadcrumbsData && Array.isArray(breadcrumbsData)) {
      const items = mapBreadcrumbsData(breadcrumbsData);
      setBreadcrumbItems(items);
    }
  }, [breadcrumbsData]); // Only re-run when breadcrumbs data or URL changes

  useEffect(() => {
    return () => {
      getResetSecertAlertData();
    };
  }, []);

  useEffect(() => {
    if (!document.getElementById('auth-script')) {
      const script = document.createElement('script');

      script.src = config('UHC_AUTH_SCRIPT');
      script.async = false;
      script.id = 'auth-script';

      document.head.appendChild(script);
    }

    const userInfoNotAvailable = !dsUserInfo?.data?.uuid;

    if (userInfoNotAvailable) {
      getDSUserInfo({ userInfo });
    } else {
      setRunCredentials(false);
    }

    // Dynatrace page tagging
    const dtrum = window?.dtrum;
    if (dtrum) {
      dtrum.setPage({
        name: 'apimarketplace:credentials',
        group: `apimarketplace-${config('APP_ENV')}`,
      });
    }
  }, []);

  useEffect(() => {
    if (runCredentials && !credentials?.data?.stage?.clientId) {
      const orgId = dsUserInfo?.data?.orgId;
      getCredentials({ userInfo, orgId });
    }
  }, [runCredentials]);

  useEffect(() => {
    if (dsUserInfo?.data?.uuid) {
      setRunCredentials(true);
    }

    if (credentials?.loading) {
      setIsLoading(true);
    }

    if (
      (dsUserInfo?.data?.status && credentials.data?.stage?.clientId) ||
      credentials.error
    ) {
      setIsLoading(false);
    }

    if (
      !dsUserInfo.loading &&
      dsUserInfo.data.uuid !== '' &&
      !isPageLoadTriggered
    ) {
      setIsPageLoadTriggered(true);
      event('NEW_PAGE_LOAD', {
        pageName: 'apimarketplace:welcome:credentials',
        siteSectionL1: 'welcome',
        siteSectionL2: 'credentials',
        dsUserInfo: dsUserInfo.data,
        userInfo,
      });
    }
  }, [dsUserInfo, credentials]);

  const closeModal = () => {
    setRegenSecretModalOpen(false);
  };

  const getClientId = () => {
    if (regenSecretEnv === 'prod') {
      return credentials?.data?.prod?.clientId;
    }

    return credentials?.data?.stage?.clientId;
  };

  const getProdBoolean = () => {
    return regenSecretEnv === 'prod';
  };

  return (
    <React.Fragment>
      <LoadingOverlay
        loadingMessage="Please wait while we retrieve your data."
        ariaLoadingLabel="Example loading aria label"
        isOpen={isLoading || credentials.error}
        isLoading={isLoading}
        statusTitle="Error Fetching Credentials"
        statusMessage="Please try to refresh the page again."
        statusIcon="error"
        width="60%"
      >
        {regenSecretModalOpen && (
          <RegenerateSecretModal
            isOpen={regenSecretModalOpen}
            closeModal={closeModal}
            clientId={getClientId()}
            regenerateEnv={regenSecretEnv}
            isProd={getProdBoolean()}
            orgId={dsUserInfo?.data?.orgId}
          />
        )}
        <Wrapper>
          {generatedSecret?.data?.alertData?.faultCode === 'AAMPCBK' && (
            <Alert
              title="Secret Regeneration Error"
              variant="error"
              inlineText={generatedSecret?.data?.alertData?.message}
            />
          )}
          <StyledFlex>
            <Container>
              <Breadcrumbs
                size="11px"
                css={{
                  fontWeight: 400,
                  'abyss-breadcrumb-current': { fontWeight: 400 },
                }}
                items={breadcrumbItems}
              />
              <Flexed>
                <PageHeadingContainer>
                  <PageHeading id="credentials-heading">
                    Credentials
                  </PageHeading>
                </PageHeadingContainer>
              </Flexed>
              <PageDesc id="credentials-description">
                You can use your client ID and secret for your APIs. You will
                have a separate set of these credentials for both your Stage and
                Production environments. Secrets will expire every 90 days. If
                you have two active secrets, you will need to delete one before
                generating a new one.{' '}
              </PageDesc>

              <BoxWrapper id="credentials-wrappper">
                <Box
                  clientId={
                    showCredentials ? credentials?.data?.stage?.clientId : ''
                  }
                  secrets={
                    showCredentials ? credentials?.data?.stage?.secrets : []
                  }
                  orgId={dsUserInfo?.data?.orgId}
                  heading="Staging"
                  showData={dsUserInfo?.data?.linkedApis?.stage?.length}
                  isSecretKeeper={showCredentials && dsUserInfo?.data?.isAdmin}
                  description="Staging credentials will be available once staging onboarding
                process is complete."
                  regenSecret={setRegenSecretModalOpen}
                  setRegenSecretEnv={setRegenSecretEnv}
                  isSecretGenerated={
                    credentials?.data?.stage?.secrets?.length > 0
                  }
                />
                <Box
                  clientId={
                    showCredentials ? credentials?.data?.prod?.clientId : ''
                  }
                  secrets={
                    showCredentials ? credentials?.data?.prod?.secrets : []
                  }
                  orgId={dsUserInfo?.data?.orgId}
                  heading="Production"
                  showData={!!dsUserInfo?.data?.linkedApis?.prod?.length}
                  isSecretKeeper={showCredentials && dsUserInfo?.data?.isAdmin}
                  description="Production credentials will be available once staging onboarding
                process is complete."
                  regenSecret={setRegenSecretModalOpen}
                  setRegenSecretEnv={setRegenSecretEnv}
                  isSecretGenerated={
                    credentials?.data?.prod?.secrets?.length > 0
                  }
                />
              </BoxWrapper>
            </Container>
          </StyledFlex>
          <br />
          <br />
          <StyledFlex>
            <Container>
              <Flexed>
                <PageHeadingContainer>
                  <PageHeading id="oAuth-token-header">OAuth Token</PageHeading>
                </PageHeadingContainer>
              </Flexed>
              <PageDesc id="oAuth-Token-description">
                Generate your OAuth Token in different environments using the
                gateway URLs below.{' '}
              </PageDesc>
              <BoxWrapper id="oAuth-Token-wrapper">
                <OAuthToken
                  oauthToken={config('OAUTH_TOKEN_URL_STAGE')}
                  heading="Staging"
                  showData={dsUserInfo?.data?.linkedApis?.stage?.length}
                  description=""
                />
                <OAuthToken
                  oauthToken={config('OAUTH_TOKEN_URL_PROD')}
                  heading="Production"
                  showData={dsUserInfo?.data?.linkedApis?.prod?.length}
                  description="Production gateway URL will be available once staging onboarding
                process is complete."
                />
              </BoxWrapper>
            </Container>
          </StyledFlex>
        </Wrapper>
      </LoadingOverlay>
    </React.Fragment>
  );
};
